import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import HeroBanner from './components/HeroBanner';
import PublicationSection from './components/PublicationSection';
import CategoryPublicationsSection from './components/CategoryPublicationsSection';
import { Publication } from '@shared/types/publication';
import { getStorage } from '@shared/utils/browsers';
import { useStore } from '@shared/hooks/useStore';
import { showQuickViewModal } from '@user/components/publication/QuickViewModal';
import BannerApi, { BannerData } from '@shared/apis/BannerApi';

const HomePage = observer(() => {
    const navigate = useNavigate();
    const { modalStore, publicationStore } = useStore();
    const { rankingPublications, totalRanking, newPublications, totalNew, recentlyViewed, totalRecentlyViewed } = publicationStore;

    const isLoggedIn = useMemo(() => !!getStorage('token'), []);
    const [bannerImageUrl, setBannerImageUrl] = useState<string | undefined>(undefined);

    const handlePublicationClick = (publication: Publication) => {
        navigate(`/publications/${publication.id}`);
    };

    const handleQuickView = (publication: Publication) => {
        showQuickViewModal(publication, modalStore);
    };

    useEffect(() => {
        const fetchBanner = async () => {
            try {
                const res = await BannerApi.getBanner();
                if (res?.data?.ok && res?.data?.data) {
                    const bannerData = res.data.data as BannerData;
                    setBannerImageUrl(bannerData.link_img);
                }
            } catch (error) {
                console.error('Failed to fetch banner:', error);
            }
        };
        fetchBanner();
    }, []);

    useEffect(() => {
        const fetchSections = async () => {
            publicationStore.fetchRankingPublications({ size: 6 });
            publicationStore.fetchNewPublications({ size: 6 });

            if (isLoggedIn) {
                publicationStore.fetchRecentlyViewedPublications({ size: 6 });
            }
        };

        fetchSections();
    }, [isLoggedIn, publicationStore]);

    return (
        <>
            <HeroBanner imageUrl={bannerImageUrl} />

            <CategoryPublicationsSection
                onPublicationClick={handlePublicationClick}
                onQuickView={handleQuickView}
            />

            <section className='bg-section-highlight'>
                <PublicationSection
                    title='ランキング'
                    publications={rankingPublications}
                    isRankingSection={true}
                    onShowMore={() => navigate('/publications/ranking')}
                    totalRecord={totalRanking}
                    onPublicationClick={handlePublicationClick}
                    onQuickView={handleQuickView}
                />
            </section>

            <PublicationSection
                title='新商品'
                publications={newPublications}
                onShowMore={() => navigate('/publications/new')}
                totalRecord={totalNew}
                onPublicationClick={handlePublicationClick}
                onQuickView={handleQuickView}
            />

            {isLoggedIn && recentlyViewed.length > 0 && (
                <PublicationSection
                    title='最近閲覧した商品'
                    publications={recentlyViewed}
                    onShowMore={() => navigate('/publications/recently')}
                    totalRecord={totalRecentlyViewed}
                    onPublicationClick={handlePublicationClick}
                    onQuickView={handleQuickView}
                />
            )}
        </>
    );
});

export default HomePage;
